import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const AdminRegister: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [municipality, setMunicipality] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    logoUrl: ''
  });

  const [admin, setAdmin] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
  });

  const handleMunicipalityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMunicipality((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAdminChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdmin((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    if (!file) {
      setMunicipality((prev) => ({ ...prev, logoUrl: '' }));
      setLogoPreview(null);
      return;
    }

    setError(null);
    setIsUploadingLogo(true);
    setLogoPreview(URL.createObjectURL(file));

    try {
      const response = await apiService.uploadMunicipalityLogo(file);
      if (!response.success || !response.data?.url) {
        throw new Error(response.error?.message || 'Logo upload failed');
      }

      setMunicipality((prev) => ({ ...prev, logoUrl: response.data!.url }));
    } catch (err) {
      setMunicipality((prev) => ({ ...prev, logoUrl: '' }));
      setError(err instanceof Error ? err.message : 'Logo upload failed');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isUploadingLogo) {
        throw new Error('Please wait for the logo upload to finish.');
      }

      if (!municipality.logoUrl) {
        throw new Error('Municipality logo is required.');
      }

      const response = await apiService.registerAdmin({
        municipality,
        admin
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Registration failed');
      }

      await login({ email: admin.email, password: admin.password });
      navigate('/live-dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            Register Your Municipality
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Create your municipality and admin account
          </p>
        </div>

        <form className="space-y-6 bg-white p-6 rounded-lg shadow-sm" onSubmit={handleSubmit}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Municipality Details</h3>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                name="name"
                placeholder="Municipality Name"
                value={municipality.name}
                onChange={handleMunicipalityChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="code"
                placeholder="Municipality Code"
                value={municipality.code}
                onChange={handleMunicipalityChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="email"
                type="email"
                placeholder="Municipality Email"
                value={municipality.email}
                onChange={handleMunicipalityChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="phone"
                placeholder="Municipality Phone"
                value={municipality.phone}
                onChange={handleMunicipalityChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="address"
                placeholder="Municipality Address"
                value={municipality.address}
                onChange={handleMunicipalityChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:col-span-2"
              />
              <input
                name="website"
                placeholder="Municipality Website"
                value={municipality.website}
                onChange={handleMunicipalityChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="logo"
                type="file"
                accept="image/*"
                required
                onChange={handleLogoChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="md:col-span-2 flex items-center gap-4">
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Municipality logo preview"
                    className="h-16 w-16 rounded object-contain border border-gray-200 bg-white"
                  />
                )}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {isUploadingLogo
                    ? 'Uploading logo...'
                    : municipality.logoUrl
                    ? 'Logo uploaded successfully.'
                    : 'Please upload a logo file.'}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Admin Details</h3>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                name="firstName"
                placeholder="First Name"
                value={admin.firstName}
                onChange={handleAdminChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="lastName"
                placeholder="Last Name"
                value={admin.lastName}
                onChange={handleAdminChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="email"
                type="email"
                placeholder="Admin Email"
                value={admin.email}
                onChange={handleAdminChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="phone"
                placeholder="Admin Phone"
                value={admin.phone}
                onChange={handleAdminChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <input
                name="password"
                type="password"
                placeholder="Admin Password"
                value={admin.password}
                onChange={handleAdminChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:col-span-2"
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Creating account...' : 'Create Municipality'}
          </button>

          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRegister;
